import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  useTheme,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Build,
  Policy,
  Timer,
  Engineering,
  DesignServices
} from '@mui/icons-material';
// Adicionando a importação do RouterLink, necessária para a funcionalidade
import { Link as RouterLink } from 'react-router-dom';


// Componentes estilizados
const HeroSection = styled(Box)(({ theme }) => ({
  // Azul escuro profissional com gradiente
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #004d40 100%)`, // Main Blue to Dark Teal
  color: 'white',
  padding: theme.spacing(12, 2),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(0, 0, 4, 4), // Borda arredondada na parte inferior
  boxShadow: theme.shadows[10],
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Padrão de rede de pontos
    background: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.3,
    zIndex: 1,
  },
  '> *': { // Garante que o conteúdo fique acima do fundo
    zIndex: 2,
    position: 'relative',
  }
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  transition: 'all 0.3s ease',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[15],
    borderColor: theme.palette.primary.light,
  }
}));

const FeatureIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.dark, // Cor mais escura para contraste
  width: 60,
  height: 60,
  marginBottom: theme.spacing(2),
}));

// Componente Footer dummy (assume-se que está disponível)
const Footer = () => (
    <Box component="footer" sx={{ p: 3, bgcolor: 'grey.900', color: 'white', textAlign: 'center' }}>
      <Container>
        <Typography variant="body2">© 2024 Fiber B2B Construction. All rights reserved.</Typography>
      </Container>
    </Box>
);


function ServicesPage() {
  const theme = useTheme();

  // Dados adaptados para Construtora/B2B (Excavação, Lançamento, Certificação)
  const services = [
    {
      icon: <Build />,
      title: "Excavation and Cable Laying",
      description: "Complete execution of trenches, ducts, and optical cable laying, ensuring end-to-end physical infrastructure, with a focus on durability and safety.",
      features: ["Legal Compliance", "Specialized In-House Team", "High Safety Standard"]
    },
    {
      icon: <Engineering />,
      title: "Fusion and Optical Certification",
      description: "Technical services for fiber fusion (splices) and OTDR certification tests to ensure transmission quality, low attenuation, and optimal network performance.",
      features: ["State-of-the-Art OTDR Testing", "Performance Certification", "As-Built Documentation"]
    },
    {
      icon: <Policy />,
      title: "Infrastructure Maintenance (SLA)",
      description: "Preventive and corrective maintenance of the deployed optical network, including emergency break repairs and scheduled inspections for network longevity.",
      features: ["Georeferenced Monitoring", "Rapid Response Time", "24h Emergency Repairs"]
    }
  ];

  // Métricas de Construção e Qualidade, não de velocidade de conexão
  const features = [
    { icon: <DesignServices />, text: "Technical Viability and Executive Project" },
    { icon: <Policy />, text: "Execution in Compliance with Regulatory Standards" },
    { icon: <Timer />, text: "Guarantee of Deadlines and Contractual SLAs" }
  ];

  return (
    <>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Chip 
            label="OUR B2B SOLUTIONS" 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)', 
              color: 'white', 
              mb: 3,
              fontWeight: 600,
              fontSize: '0.8rem' 
            }} 
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: 36, md: 54 } 
            }}
          >
            ENGINEERING AND CONSTRUCTION OF OPTICAL NETWORKS
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 400, 
              mb: 5,
              opacity: 0.9,
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            Fiber optic infrastructure for Operators, Internet Service Providers, and Large Enterprises. **Projects, Execution, and Heavy Maintenance.**
          </Typography>
          
          {/* BOTÕES REMOVIDOS DA SEÇÃO HERO */}
          
        </Container>
      </HeroSection>

      {/* Features Section - Valores da Construtora */}
      <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
           <Typography 
            variant="h4" 
            align="center"
            sx={{ 
              fontWeight: 700, 
              mb: 5,
              color: 'text.primary'
            }}
          >
            Our Commitment to Infrastructure Quality
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper elevation={3} sx={{ textAlign: 'center', p: 4, height: '100%', borderRadius: 3 }}>
                  <Avatar
                    sx={{
                      backgroundColor: 'primary.dark',
                      color: 'white',
                      width: 70,
                      height: 70,
                      mx: 'auto',
                      mb: 2,
                      boxShadow: theme.shadows[5]
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {feature.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section - Detalhamento dos Serviços de Campo */}
      <Box sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 700, 
                letterSpacing: 2,
                fontSize: '1rem'
              }}
            >
              CRITICAL DEPLOYMENT STAGES
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: 30, md: 40 }
              }}
            >
              Engineering and Field Services
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary', 
                maxWidth: 750, 
                mx: 'auto',
                fontSize: '1.1rem'
              }}
            >
              Our specialty is providing the robust foundation for operators and large companies to deliver the best connection experience to their end customers.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ServiceCard elevation={2}>
                  <FeatureIcon>
                    {service.icon}
                  </FeatureIcon>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      color: 'primary.dark'
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary', 
                      mb: 3,
                      lineHeight: 1.7
                    }}
                  >
                    {service.description}
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    {service.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box 
                          sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            backgroundColor: 'primary.main', 
                            mr: 2,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section - Botão Modificado */}
      <Box sx={{ py: 10, backgroundColor: theme.palette.grey[900], color: 'white' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
            READY TO START YOUR NEXT FIBER PROJECT?
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, mb: 4, opacity: 0.9 }}>
            Maximize the quality of your infrastructure. Contact our engineering team and request technical details for your quotation.
          </Typography>
          <RouterLink to="/contact-us" style={{ textDecoration: 'none' }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                backgroundColor: theme.palette.secondary.main, // Usando uma cor de destaque
                color: 'white',
                fontWeight: 700,
                px: 6,
                py: 1.5,
                mt: 2,
                borderRadius: 3,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.dark,
                }
              }}
            >
              CONTACT US
            </Button>
          </RouterLink>
        </Container>
      </Box>

      {/* O componente Footer é mantido no final */}
      <Footer />
    </>
  );
}

export default ServicesPage;